import {
  inject,
  onBeforeUnmount,
  onMounted,
  ref,
  type InjectionKey,
  type Ref,
} from 'vue';
import Client from './websocket';

/**
 * Provided by the Connect shell. Sub-apps inject this to share the single
 * multiplexed WebSocket connection — they should NOT create their own
 * `Client` instances. Returns `null` in standalone mode where the shell
 * isn't present; callers should skip socket use gracefully (or fall back
 * to legacy direct WS in that case).
 */
export const NxSocketKey: InjectionKey<Client> = Symbol('NxSocket');

export function useNxSocket(): Client | null {
  return inject(NxSocketKey, null);
}

export type NxSocketStatus = 'disconnected' | 'connecting' | 'ready';

/**
 * Reactive view on the shared socket's lifecycle. `connecting` covers the
 * window between the TCP-level open and the master mux `ready` frame; `ready`
 * means the hello/handshake completed and channels can send. Falls back to
 * `disconnected` whenever the socket isn't provided (standalone mode).
 */
export function useNxSocketStatus(): { status: Ref<NxSocketStatus> } {
  const client = inject(NxSocketKey, null);
  const status = ref<NxSocketStatus>('disconnected');

  function refresh() {
    if (!client) {
      status.value = 'disconnected';
      return;
    }
    if (client.isReady()) status.value = 'ready';
    else if (client.isConnected()) status.value = 'connecting';
    else status.value = 'disconnected';
  }

  const unsubs: Array<() => void> = [];

  onMounted(() => {
    if (!client) return;
    refresh();
    unsubs.push(client.on('connected', refresh));
    unsubs.push(client.on('ready', refresh));
    unsubs.push(client.on('disconnected', refresh));
  });

  onBeforeUnmount(() => {
    while (unsubs.length) {
      const u = unsubs.pop();
      if (u) u();
    }
  });

  return { status };
}
